import { useState } from 'react';
import { useEmails } from '../hooks/useEmails';
import { ref, remove } from 'firebase/database';
import { database } from '../config/firebase';
import { DateTime } from 'luxon';
import toast from 'react-hot-toast';
import ConfirmDialog from './ConfirmDialog';
import './EmailList.css';

function EmailList({ folder, searchTerm, onEmailClick, user }) {
  const { emails, loading, error, refresh } = useEmails(user, folder);
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDeleteClick = () => {
    if (selectedEmails.size === 0) {
      return;
    }
    setShowDeleteConfirm(true);
  };

  const handleDeleteSelected = async () => {
    if (selectedEmails.size === 0) {
      return;
    }

    const count = selectedEmails.size;
    setShowDeleteConfirm(false);
    setDeleting(true);

    try {
      const deletePromises = Array.from(selectedEmails).map(async (emailId) => {
        const emailRef = ref(database, `emails/${user.uid}/${folder}/${emailId}`);
        await remove(emailRef);
      });

      await Promise.all(deletePromises);
      
      // Clear selection after successful deletion
      setSelectedEmails(new Set());
      setSelectAll(false);
      
      // Real-time listener will automatically update the list
      toast.success(`Email${count > 1 ? 's' : ''} deleted successfully`);
    } catch (error) {
      console.error('Error during deletion:', error);
      toast.error(`Failed to delete email${count > 1 ? 's' : ''}: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const formatTimestamp = (ts) => {
    return DateTime.fromISO(ts, { zone: 'utc' })
      .setZone(Intl.DateTimeFormat().resolvedOptions().timeZone)
      .toFormat("hh:mm a, dd MMMM, yyyy");
  };

  const filteredEmails = emails.filter(email => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      email.subject?.toLowerCase().includes(term) ||
      email.from_email?.toLowerCase().includes(term) ||
      email.to_email?.toLowerCase().includes(term)
    );
  });

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedEmails(new Set(filteredEmails.map(e => e.id)));
    } else {
      setSelectedEmails(new Set());
    }
  };

  const handleEmailSelect = (emailId, checked) => {
    const newSelected = new Set(selectedEmails);
    if (checked) {
      newSelected.add(emailId);
    } else {
      newSelected.delete(emailId);
    }
    setSelectedEmails(newSelected);
    setSelectAll(newSelected.size === filteredEmails.length);
  };

  if (loading) {
    return (
      <div className="email-list">
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading emails...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="email-list">
        <div className="empty-state">
          <i className="fas fa-exclamation-circle"></i>
          <h3>Error Loading Emails</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (filteredEmails.length === 0) {
    return (
      <div className="email-list">
        <div className="email-list-header">
          <div className="email-list-actions">
            <div className="action-group">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            </div>
          </div>
        </div>
        <div className="empty-state">
          <i className={`fas ${folder === 'inbox' ? 'fa-inbox' : 'fa-paper-plane'}`}></i>
          <h3>{folder === 'inbox' ? 'Your inbox is empty' : 'No sent emails'}</h3>
          <p>
            {folder === 'inbox'
              ? 'When you receive emails, they will appear here.'
              : 'Emails you send will appear here.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="email-list">
      <div className="email-list-header">
        <div className="email-list-actions">
          <div className="action-group">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
            <button 
              className="icon-button" 
              title="Refresh"
              onClick={async () => {
                setRefreshing(true);
                refresh();
                // Reset refreshing state after a short delay
                setTimeout(() => setRefreshing(false), 500);
              }}
            >
              <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
            </button>
            <button
              className="icon-button"
              title="Delete"
              disabled={selectedEmails.size === 0 || deleting}
              onClick={handleDeleteClick}
            >
              <i className={`fas fa-trash ${deleting ? 'fa-spin' : ''}`}></i>
            </button>
          </div>
        </div>
        <div className="email-list-pagination">
          <span>1-{filteredEmails.length} of {filteredEmails.length}</span>
        </div>
      </div>
      
      <div className="emails-list">
        {filteredEmails.map((email) => {
          const isUnread = folder === 'inbox' && !email.read;
          return (
            <div
              key={email.id}
              className={`email-item ${isUnread ? 'unread' : ''} ${selectedEmails.has(email.id) ? 'selected' : ''}`}
              onClick={(e) => {
                // Don't open email if clicking on checkbox or its container
                if (e.target.type === 'checkbox' || e.target.closest('.email-checkbox')) {
                  return;
                }
                onEmailClick(email);
              }}
            >
              <div 
                className="email-checkbox-wrapper"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  className="email-checkbox"
                  checked={selectedEmails.has(email.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleEmailSelect(email.id, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="email-content">
                <div className="sender">
                  {folder === 'inbox' ? email.from_email : email.to_email}
                </div>
                <div className="subject">{email.subject || '(No Subject)'}</div>
                <div className="timestamp">{formatTimestamp(email.timestamp)}</div>
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteSelected}
        title="Delete Emails"
        message={`Are you sure you want to delete ${selectedEmails.size} email${selectedEmails.size > 1 ? 's' : ''}? This action cannot be undone.`}
      />
    </div>
  );
}

export default EmailList;
